// Popover — the one definition every floating menu in the app paints itself with.
//
// W52.3. Five popovers existed and none of them matched:
//
//   PillNavMenu          navy  · 20px · shadow-pop  · 16px gap · 34px icon tile
//   AvatarMenu           white · 16px · shadow-pop  ·  8px gap · bare 16px icon
//   WishDetailActionBar  white · 16px · shadow-pop  ·  8px gap · bare 16px icon
//   AnniversaryMoreMenu  paper ·  8px · shadow-modal ·  6px gap · md rows
//   LayoutEditor         paper · 16px · shadow-modal ·  4px gap · bare, w-full
//
// Three materials, three radii, two shadows, four offsets — on screens one tap
// apart. The reference is `PillNavMenu`, which the user named: navy is
// `DESIGN.md §1.5`'s L3 chrome layer, and a floating panel is chrome, never
// paper. **That reverses the W52.0 D-6 choice of a white card for
// `AvatarMenu`** — it was a deliberate call, so ADR-0173 records why it is being
// reversed rather than quietly overwriting it.
//
// ## Constants, not a wrapper component
//
// This file exports class **strings**, not a `<PopoverPanel>` that owns
// `role="menu"` and the child structure. The reasons are concrete:
//
//   - `AnniversaryMoreMenu` is a **Radix** popover, portalled and positioned by
//     Radix. Wrapping it would fight the library.
//   - `LayoutEditor`'s add-block menu is a `<ul>` whose rows are `<li role="none">`
//     wrapping a `<button role="menuitem">`. A wrapper that assumed
//     `button > span > tile` would have to rewrite that.
//   - `WishDetailActionBar` has a row that is a `HoldToConfirmButton`, not a
//     `button`.
//   - `AnniversaryMoreMenu` has one too.
//
// So each call site keeps its own semantics and applies the shared constants.
// That is enough to stop the drift, because **the guard asserts the constants
// are used** — and nobody changes five radii together by accident, they change
// one and the other four stay.

import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react";

/**
 * The gap between a trigger and its panel, in px.
 *
 * 16, because that is what `PillNavMenu` already used and it is the only one of
 * the four values that had been measured against the bar directly beneath it.
 * The others — 4, 6 and 8 — were each a local guess, and a 4px gap reads as a
 * rendering bug rather than as "attached to".
 */
export const POPOVER_GAP = 16;

/**
 * How much room a side needs before the panel is allowed to keep it.
 *
 * 160px is about the tallest panel in the app (the nav popper's four rows plus
 * its group label), so it is the point at which opening that way stops being
 * readable rather than the point at which the panel leaves the screen.
 */
const MIN_FLIP_ROOM = 160;

/**
 * The gap, as inline style — and it has to be inline.
 *
 * `` `mb-[${POPOVER_GAP}px]` `` is the obvious way to write this and it emits
 * **nothing**. Tailwind's scanner reads source text, and the source text is
 * `mb-[${POPOVER_GAP}px]` — the number only exists at runtime, in a string the
 * scanner never saw. The class is not in the generated stylesheet, the panel
 * gets no margin, and every popover sits flush against its trigger.
 *
 * This is `ADR-0170`'s third instance, in a new costume: `bg-lilac` and
 * `bg-surface-plain` were class *names* that did not exist; this is a class that
 * exists in the source and does not exist in the build. The live probe measured
 * the result — gaps of **0, 5 and 6px** where 16 was asked for — and no unit
 * test could have caught it, because the class string is correct and only the
 * emitted CSS is not.
 *
 * Inline style is the right answer here precisely because it is not the
 * scanner's business. `AGENTS.md §3 #17` bans hardcoded **colour**; a gap in
 * px that comes from one shared constant is the opposite of hardcoded.
 */
export const gapUp = (): React.CSSProperties => ({ marginBottom: POPOVER_GAP });
export const gapDown = (): React.CSSProperties => ({ marginTop: POPOVER_GAP });

/**
 * The panel. Material, radius, border, shadow and padding in one string so that
 * changing any of them is **one** edit rather than five.
 *
 * - `bg-chrome-navy` — `DESIGN.md §1.5`: navy is the L3 navigation layer, and a
 *   floating panel sits in that layer whichever page it opens over.
 * - `rounded-[20px]` — a panel is a panel, not a chip. Three of the five were
 *   `rounded-2xl` (16px) and one was `rounded-lg` (8px, which read as a chip).
 * - `p-2` + `gap-0.5` — the nav's own rhythm.
 */
export const POPOVER_PANEL_CLASS =
  "bg-chrome-navy border border-white/15 shadow-pop rounded-[20px] p-2 flex flex-col gap-0.5";

/**
 * One row: a 44pt target with a 14px radius.
 *
 * The tile is what was missing everywhere except the nav's popper, and it is
 * what makes three rows read as three **objects** rather than three lines of
 * text. `text-white/85`, `hover:bg-white/10` and the selected lilac wash are
 * the nav's own values — one selected colour in the whole navigation.
 */
export const POPOVER_ROW_CLASS =
  "flex min-h-11 w-full items-center gap-2.5 rounded-[14px] px-2.5 py-2 " +
  "text-left text-white/85 transition-colors duration-quick ease-quiet press hover:bg-white/10";

/** The 34px icon tile the rows sit against. */
export const POPOVER_TILE_CLASS =
  "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[11px] " +
  "bg-white/10 text-white";

/** The selected tile — the same lilac `.nav-slot` spends. */
export const POPOVER_TILE_SELECTED_CLASS = "bg-surface-lilac text-ink-1";

export type PopoverAlign = "left" | "right" | "center";

export interface PopoverPlacement {
  align: PopoverAlign;
  side: "top" | "bottom";
  /**
   * Inline style for a panel that is a DOM child of a `relative` trigger.
   * `undefined` when centred, because centring is done in CSS.
   */
  style?: React.CSSProperties;
  /** For a **portalled** panel (Radix): which `align` / `side` to hand it. */
  radixAlign: "start" | "center" | "end";
  radixSide: "top" | "bottom";
}

/**
 * Decide which edge of its trigger a panel should hang from.
 *
 * The rule, stated once: **the panel's near edge meets the trigger's near
 * edge** — left half of the screen → left edges meet; right half → right edges
 * meet; a trigger within 8px of centre → centre, because that is the only
 * answer keeping both of its margins equal.
 *
 * `side` is an argument rather than a measurement: "which way does this one
 * open" is a design decision (a menu on a bottom bar opens up; a dropdown
 * under a field opens down), while "which edge does it hang from" is
 * arithmetic on where the trigger happens to be.
 *
 * Why it is stated at all: the previous state was not a rule but four
 * independent choices — `right-0` on one, `left-0` on another,
 * `left-1/2 -translate-x-1/2` on a third, Radix `align="end"` on a fourth. The
 * live probe showed what that costs: moving a trigger from the right end of a
 * bar to the left end left its panel anchored `right-0`, and two thirds of a
 * 180px menu went off the edge of a 390px screen. **A popover that opens
 * successfully can still be one you cannot read.**
 */
export function usePopoverPlacement(
  triggerRef: RefObject<HTMLElement | null>,
  side: "top" | "bottom" = "bottom",
  /**
   * Force an alignment instead of measuring.
   *
   * `PillNavMenu` needs it: its popper is centred on the **nav**, not on the
   * 更多 trigger, which sits at ~87% across a four-slot pill and would measure
   * as "right". Centring on the bar is the documented behaviour and the better
   * one — it is what keeps a 240px panel on the bar instead of hanging off one
   * end of it.
   */
  forceAlign?: PopoverAlign,
): PopoverPlacement {
  const [align, setAlign] = useState<PopoverAlign>(forceAlign ?? "center");
  const [placed, setPlaced] = useState<"top" | "bottom">(side);

  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    if (forceAlign) {
      setAlign(forceAlign);
    } else {
      const centre = r.left + r.width / 2;
      setAlign(Math.abs(centre - window.innerWidth / 2) < 8
        ? "center"
        : centre < window.innerWidth / 2 ? "left" : "right");
    }

    // Flip rather than run off the edge.
    //
    // W52.4 — **these two comparisons were written swapped.** A panel asked for
    // `bottom` flipped to `top` when its trigger sat near the **top** of the
    // screen — precisely where there is most room below — and a panel asked for
    // `top` flipped to `bottom` when its trigger sat near the **bottom**, which is
    // the only place a bottom bar can be. So the flip fired on all four call sites
    // and reported the opposite of the truth: the avatar menu (top of screen,
    // opens downward) carried `data-side="top"` while rendering below it, and the
    // nav popper (bottom of screen, opens upward) carried `data-side="bottom"` and
    // — because `PillNavMenu` reads `placement.side` for its `transform-origin` —
    // scaled *toward* the bar it is supposed to be growing away from.
    //
    // The live probe printed `data-side` on every line and read **green** anyway,
    // because it never compared that attribute against where the panel actually
    // painted. A flip decision has to be about the side being asked for; the
    // opposite side's room is not the question.
    const roomBelow = window.innerHeight - r.bottom;
    const roomAbove = r.top;
    setPlaced(
      side === "bottom"
        ? roomBelow < MIN_FLIP_ROOM ? "top" : "bottom"
        : roomAbove < MIN_FLIP_ROOM ? "bottom" : "top",
    );
  }, [triggerRef, side, forceAlign]);

  // `useLayoutEffect`, not `useEffect`: positioning after paint means the panel
  // renders one frame on the wrong edge, which reads as a jump.
  useLayoutEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [measure]);

  const style: React.CSSProperties | undefined =
    align === "center"
      ? undefined
      : align === "left"
        ? { left: 0 }
        : { right: 0 };

  return {
    align,
    side: placed,
    style,
    radixAlign: align === "left" ? "start" : align === "right" ? "end" : "center",
    radixSide: placed === "top" ? "top" : "bottom",
  };
}

/**
 * Did this panel stay inside the viewport?
 *
 * For the live probe, which has no React tree to ask. Material tokens cannot
 * answer it: a panel can carry every correct class and still be unreadable if
 * it is clipped.
 */
export function popoverFitsViewport(el: Element | null): boolean {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.left >= -1 && r.right <= window.innerWidth + 1;
}

export { useRef };
